import type { McpServerType, McpToolProperties, McpToolPolicy } from '@server/mcp';
import type { McpConnectorUI } from '../../components/dashboard/user-settings/mcp-connectors/types';
import { useSweepApi } from '../sweep/useSweepApi';
import { useCallback } from 'react';

export const useMcpConnectorsApiFacade = () => {
  const sweepApi = useSweepApi();

  return {
    get_connectors: useCallback(async (): Promise<McpConnectorUI[]> => {
      const response = await sweepApi.get('/mcp-connectors');
      return response.data;
    }, [sweepApi]),

    post_connector: useCallback(
      async (serverType: McpServerType, serverUrl?: string): Promise<McpConnectorUI> => {
        const response = await sweepApi.post('/mcp-connectors', {
          serverType,
          ...(serverUrl && { serverUrl }),
        });
        return response.data;
      },
      [sweepApi],
    ),

    // TODO SWEEP-10341 - this is a temporary hack for Varonis, will remove once Workato integration is live
    post_workato_token_connector: useCallback(
      async (serverUrl: string, apiToken: string): Promise<McpConnectorUI> => {
        const response = await sweepApi.post('/mcp-connectors/workato-with-token', {
          serverUrl,
          apiToken,
        });
        return response.data;
      },
      [sweepApi],
    ),

    delete_connector: useCallback(
      async (serverId: string): Promise<void> => {
        await sweepApi.delete(`/mcp-connectors/${serverId}`);
      },
      [sweepApi],
    ),

    getOAuthUrl: useCallback(
      async (serverId: string): Promise<{ url: string }> => {
        const response = await sweepApi.get(`/mcp-connectors/${serverId}/oauth/url`);
        return response.data;
      },
      [sweepApi],
    ),

    handleOAuthCallback: useCallback(
      async (serverId: string, code: string): Promise<void> => {
        await sweepApi.post(`/mcp-connectors/${serverId}/oauth/callback`, { code });
      },
      [sweepApi],
    ),

    delete_connection: useCallback(
      async (connectionId: string): Promise<void> => {
        await sweepApi.delete(`/mcp-connectors/connections/${connectionId}`);
      },
      [sweepApi],
    ),

    get_connectionToolPolicies: useCallback(
      async (connectionId: string): Promise<McpToolProperties[]> => {
        const response = await sweepApi.get(
          `/mcp-connectors/connections/${connectionId}/tool-policies`,
        );
        return response.data;
      },
      [sweepApi],
    ),

    put_connectionToolPolicies: useCallback(
      async (
        connectionId: string,
        toolPolicies: { name: string; policy: McpToolPolicy }[],
      ): Promise<void> => {
        await sweepApi.put(`/mcp-connectors/connections/${connectionId}`, { toolPolicies });
      },
      [sweepApi],
    ),
  };
};
